/**
 * DTOs for the consolidated User Details endpoints (PEG-799 backend, PEG-813 FE).
 *
 * TEMPORARY, NOT THE FINAL CONTRACT (per PEG-813's API-seam criterion): confirmed
 * against the Notion "User details page" doc 2026-08-17, but PEG-799 is still in
 * progress. If the shape moves, only this file and `sections/*.ts` change — no
 * component reads these directly.
 *
 * Every section type is field-complete now, even though some panels that read
 * them are still stubs.
 */

import type { MetaDataObj } from 'src/helper/shared-types';

export type LookupOption = { value: string; label: string };

export type NullableLookupOption = { value: string | null; label: string | null };

export type AccountRead = {
  first_name: string | null;
  last_name: string | null;
  middle_name: string | null;
  account_status: string | null;
  email: string | null;
  phone_number: string | null;
  address: {
    street_address: string | null;
    apt_suite_unit: string | null;
    city: string | null;
    zip_code: string | null;
    state_code?: string | null;
    state: NullableLookupOption;
    country: NullableLookupOption;
    latitude: number | null;
    longitude: number | null;
    google_place_id: string | null;
  } | null;
  roles?: Array<{ value: string; label: string }>;
  /** Coordinator targets only — `{id,name}`, already resolved by the API; unlike `AccountWrite.markets`, never bare ids. */
  markets?: Array<{ id: string; name: string }>;
  company_name?: string;
  gl_insurance_expire_at?: string;
};

/** `account`, as the PATCH payload expects it — flat ids, no `role`. */
export type AccountWrite = {
  first_name: string;
  last_name: string;
  middle_name: string | null;
  account_status: string;
  email: string;
  phone_number: string;
  address: {
    street_address: string;
    apt_suite_unit: string | null;
    city: string;
    state_id: string;
    country_id: string;
    zip_code: string;
    latitude: number | null;
    longitude: number | null;
    google_place_id: string;
  };
  /** Coordinator targets only — ids, not `{id,name}`. */
  markets?: string[];
  /** Vendor targets only. */
  company_name?: string;
  /** Vendor targets only. Date only, e.g. `2027-01-15`. */
  gl_insurance_expire_at?: string;
};

/** Identical shape on read and write, every role. */
export type NotificationsSection = {
  email: boolean;
  sms: boolean;
  push: boolean;
};

/** Identical shape on read and write. Technician and vendor targets only. */
export type SkillSetSection = {
  selected_problem_category_ids: string[];
  selected_sub_category_ids: string[];
  selected_diagnosis_ids: string[];
};

export type WorkingHoursDay = { from: string; to: string; isEnabled: boolean };

/** The break's own flag — `isLunchBreakEnabled`, never the weekday `isEnabled`. */
export type WorkingHoursLunchBreak = {
  from: string;
  to: string;
  isLunchBreakEnabled: boolean;
  duration: number;
};

export type WorkingHoursSchedule = {
  monday: WorkingHoursDay;
  tuesday: WorkingHoursDay;
  wednesday: WorkingHoursDay;
  thursday: WorkingHoursDay;
  friday: WorkingHoursDay;
  saturday: WorkingHoursDay;
  sunday: WorkingHoursDay;
  lunch_time_break: WorkingHoursLunchBreak;
};

/**
 * A block-out window, exactly as confirmed — technician targets only. Editable, but
 * **never part of the `working_hours` payload**: each add/edit/delete commits
 * against its own existing endpoint.
 */
export type BlockOutWindow = {
  technician_appointment_id: string;
  note: string;
  is_block_out_all_day: 0 | 1;
  date: string;
  start_time: string;
  end_time: string;
  timezone: string;
  /** Present only on a window linked to a work order; omitted entirely otherwise. */
  work_order?: { work_order_id: string; work_order_number: string };
};

/**
 * `working_hours`, as the GET response gives it — the schedule plus the block-out
 * windows, which are editable but **never part of the `working_hours` payload**.
 */
export type WorkingHoursRead = {
  schedule: WorkingHoursSchedule;
  block_out_windows: BlockOutWindow[];
};

/** `working_hours`, as the PATCH payload expects it — schedule only, no block-out. */
export type WorkingHoursWrite = {
  schedule: WorkingHoursSchedule;
};

/** Vendor targets only. `null` when the vendor has no `VendorServiceArea` record yet. */
export type ServiceAreaRead = {
  vendor_id: number;
  zip_codes: string[];
  radius: string;
} | null;

/** Vendor targets only. `radius` is the miles picked to produce `zip_codes`, sent alongside it. */
export type ServiceAreaWrite = {
  zip_codes: string[];
  radius: string;
};

/**
 * `GET /api/users/{userUuid}` — role-bucketed. `account` is always present;
 * every other key is present only for the roles that carry it.
 * `notifications` is optional rather than guaranteed: the backend
 * branch currently backing this endpoint only returns `account` for now.
 * Optional here rather than modelled as a discriminated union, so
 * `roleConfig.ts` — not this file — owns which keys a role expects.
 */
export type UserDetailsResponse = {
  account: AccountRead;
  notifications?: NotificationsSection;
  skill_set?: SkillSetSection;
  working_hours?: WorkingHoursRead;
  service_area?: ServiceAreaRead;
};

/**
 * `PATCH /api/users/{userUuid}/details` — partial update. Include a top-level key
 * ONLY if that section actually changed; omit unchanged keys entirely (never
 * `null`, never `{}`). `buildUpdatePayload.ts` is what actually omits them —
 * this type just says every key is optional.
 */
export type UpdateUserDetailsPayload = {
  account?: AccountWrite;
  notifications?: NotificationsSection;
  skill_set?: SkillSetSection;
  working_hours?: WorkingHoursWrite;
  service_area?: ServiceAreaWrite;
};

/**
 * `GET /api/users` list-item shape — the LEGACY shape, still returned by the
 * list endpoint today. Deliberately NOT unified with `UserDetailsResponse`
 * (the detail endpoint's shape): the backend hasn't migrated the list
 * endpoint's keys yet, and forcing a shared shape now would just be a
 * client-side fiction. `account_info.user_id` is the id every cache in this
 * slice keys off.
 */
export type UserListItem = {
  account_info: {
    user_id: string;
    first_name: string;
    last_name: string;
    middle_name: string | null;
    email: string;
    is_active: boolean;
    current_roles: Array<{ value: string; label: string }>;
    is_technician: boolean;
  };
  profile_pic: string | null;
  last_activity: string | null;
  group: string | null;
};

/**
 * One user's cache entry — merged in piecemeal by whichever of the list GET,
 * the detail GET, or the detail PATCH last wrote to it. Because the list and
 * detail endpoints don't share keys yet (see `UserListItem`), an entry can
 * carry both shapes' fields side by side; each reader (`UsersPanel.tsx` vs.
 * the detail page) only reads its own shape's fields.
 */
export type SettingsUserRecord = Partial<UserListItem> & Partial<UserDetailsResponse>;

export type SettingsUsersState = {
  list: { ids: string[]; meta: MetaDataObj };
  source: { [userId: string]: SettingsUserRecord };
  isListLoading: boolean;
  isLoading: boolean;
  isSaving: boolean;
};

export const settingsUsersInitialState: SettingsUsersState = {
  list: {
    ids: [],
    meta: {
      current_page: 0,
      from: 0,
      last: '',
      next: '',
      per_page: 0,
      prev: '',
      to: 0,
      total: 0,
    },
  },
  source: {},
  isListLoading: false,
  isLoading: false,
  isSaving: false,
};

// ---- Roles (Settings → Users → User roles) ----

export type RoleData = {
  role_id: string;
  name: string;
  parent_role_id: string;
  can_modify: boolean;
  users_count: number;
  description: string;
};

export type RoleInfo = {
  isLoading: boolean;
  data: {
    data: Array<string>;
    meta: MetaDataObj;
  };
  source: {
    [key: string]: RoleData;
  };
};

/** One permission inside a feature group, as returned by both permission endpoints. */
export type RolePermission = {
  permission_id: string;
  name: string;
  label: string;
};

/** One feature's worth of permissions — `GET /api/permissions`'s array shape. */
export type PermissionFeatureGroup = {
  feature_id: string;
  name: string;
  slug: string;
  permissions: RolePermission[];
};

/** `GET /api/roles/{id}` — a single role's name, description and granted permissions. */
export type RoleDetailResponse = {
  role_id: string;
  name: string;
  parent_role_id: string | null;
  description: string | null;
  can_modify: boolean;
  permissions: RolePermission[];
};

export type RolesState = {
  roles: RoleInfo;
};

export const rolesInitialState: RolesState = {
  roles: {
    isLoading: false,
    data: {
      data: [],
      meta: {
        current_page: 0,
        from: 0,
        last: '',
        next: '',
        per_page: 0,
        prev: '',
        to: 0,
        total: 0,
      },
    },
    source: {},
  },
};
